/**
 * Unified API Client for AI Recruiter
 * Centralized configuration for all API calls with CORS handling
 */
import { buildApiUrl } from '@/config/api'

interface ApiConfig {
  baseUrl: string;
  headers: Record<string, string>;
  credentials: RequestCredentials;
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

class ApiClient {
  private config: ApiConfig;
  private isLocalhost: boolean;

  constructor() {
    // Detect if running on localhost
    this.isLocalhost = typeof window !== 'undefined' && 
      (window.location.hostname === 'localhost' || 
       window.location.hostname === '127.0.0.1');

    // Set base URL based on centralized configuration
    const baseUrl = this.resolveBaseUrl();
    
    this.config = {
      baseUrl,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      // IMPORTANT: Use 'omit' since API uses Bearer tokens, not cookies
      credentials: 'omit' as RequestCredentials
    };
  }

  private resolveBaseUrl(): string {
    const url = buildApiUrl('');
    return url.endsWith('/') ? url.slice(0, -1) : url;
  }

  /**
   * Make API request with proper error handling and CORS support
   */
  async request<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    // Build full URL using centralized helper
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    const baseUrl = this.config.baseUrl || this.resolveBaseUrl();
    const sanitizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = normalizedEndpoint ? `${sanitizedBase}/${normalizedEndpoint}` : sanitizedBase;

    // Merge headers safely across different shapes of HeadersInit
    const mergedHeaders = new Headers(this.config.headers as HeadersInit);
    if (options.headers) {
      const incoming = new Headers(options.headers as HeadersInit);
      incoming.forEach((value, key) => mergedHeaders.set(key, value));
    }

    // Add auth token if available
    const token = this.getAuthToken();
    if (token) {
      mergedHeaders.set('Authorization', `Bearer ${token}`);
    }

    const requestOptions: RequestInit = {
      ...options,
      headers: Object.fromEntries(mergedHeaders.entries()),
      credentials: this.config.credentials,
      mode: 'cors' as RequestMode,
    };

    try {
      console.log(`[API] ${options.method || 'GET'} ${url}`);
      
      const response = await fetch(url, requestOptions);
      
      // Handle non-JSON responses
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const text = await response.text();
        return {
          success: response.ok,
          data: text as any,
          message: response.ok ? 'Success' : 'Error'
        };
      }

      const data = await response.json();
      
      // Handle API Gateway errors
      if (!response.ok) {
        return {
          success: false,
          error: data.error || data.message || `HTTP ${response.status}`,
          message: data.message
        };
      }

      return {
        success: true,
        data: data.data || data,
        message: data.message
      };
    } catch (error) {
      console.error('[API] Request failed:', error);
      
      // Handle CORS errors specifically
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        return {
          success: false,
          error: 'CORS error or network issue. Please check API Gateway configuration.',
          message: 'Failed to connect to API'
        };
      }

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        message: 'Request failed'
      };
    }
  }

  /**
   * GET request
   */
  async get<T = any>(endpoint: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    const cleanedParams = params
      ? Object.fromEntries(
          Object.entries(params).filter(
            ([, value]) => value !== undefined && value !== null && value !== ''
          )
        )
      : undefined;
    const queryString = cleanedParams && Object.keys(cleanedParams).length > 0
      ? '?' + new URLSearchParams(cleanedParams).toString()
      : '';
    return this.request<T>(endpoint + queryString, {
      method: 'GET'
    });
  }

  /**
   * POST request
   */
  async post<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * PUT request
   */
  async put<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * DELETE request
   */
  async delete<T = any>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'DELETE'
    });
  }

  /**
   * PATCH request
   */
  async patch<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * Get auth token from storage
   */
  private getAuthToken(): string | null {
    if (typeof window === 'undefined') return null;
    // Check for both 'token' and 'auth_token' keys for backward compatibility
    return localStorage.getItem('token') ||
           localStorage.getItem('auth_token') ||
           sessionStorage.getItem('token') ||
           sessionStorage.getItem('auth_token');
  }

  /**
   * Set auth token
   */
  setAuthToken(token: string, persistent: boolean = true): void {
    if (typeof window === 'undefined') return;

    if (persistent) {
      // Store in both keys for compatibility
      localStorage.setItem('token', token);
      localStorage.setItem('auth_token', token);
    } else {
      sessionStorage.setItem('token', token);
      sessionStorage.setItem('auth_token', token);
    }
  }

  /**
   * Clear auth token
   */
  clearAuthToken(): void {
    if (typeof window === 'undefined') return;
    // Clear both keys for complete cleanup
    localStorage.removeItem('token');
    localStorage.removeItem('auth_token');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('auth_token');
  }

  /**
   * Update base URL (for testing different environments)
   */
  setBaseUrl(url: string): void {
    this.config.baseUrl = url.endsWith('/') ? url.slice(0, -1) : url;
  }

  /**
   * Get current base URL
   */
  getBaseUrl(): string {
    return this.config.baseUrl;
  }

  // Domain-specific API methods

  /**
   * Authentication
   */
  async login(email: string, password: string): Promise<ApiResponse> {
    return this.post('/auth/login', { email, password });
  }

  async googleLogin(credential: string): Promise<ApiResponse> {
    return this.post('/auth/google-login', { credential });
  }

  /**
   * Teams
   */
  async createTeam(data: any): Promise<ApiResponse> {
    // Use /teams/create to avoid Mangum base route bug (405 error)
    return this.post('/teams/create', data);
  }

  async getTeams(): Promise<ApiResponse> {
    // Use /teams/list to avoid Mangum base route bug (405 error)
    return this.get('/teams/list');
  }

  async getTeamMembers(teamId: string, excludeCurrentUser?: boolean): Promise<ApiResponse> {
    return this.get(`/teams/${teamId}/members`, excludeCurrentUser ? { exclude_current: 'true' } : undefined);
  }

  async getTeamInvitations(type: string): Promise<ApiResponse> {
    return this.get('/team-invitations', { type });
  }

  async acceptTeamInvitation(token: string, message?: string): Promise<ApiResponse> {
    return this.post(`/team-invitations/${token}/accept`, message ? { response_message: message } : {});
  }

  async declineTeamInvitation(token: string, message?: string): Promise<ApiResponse> {
    return this.post(`/team-invitations/${token}/decline`, message ? { response_message: message } : {});
  }

  /**
   * Candidates
   */
  async shareCandidate(candidateId: string, data: any): Promise<ApiResponse> {
    return this.post(`/candidates/${candidateId}/share`, data);
  }

  /**
   * Interviews
   */
  async getInterviews(params?: any): Promise<ApiResponse> {
    return this.get('/interviews/list', params);
  }

  async scheduleInterview(data: any): Promise<ApiResponse> {
    return this.post('/interviews/create', data);
  }

  async scheduleVoiceInterview(data: any): Promise<ApiResponse> {
    return this.post('/interviews/voice', data);
  }

  async shareInterviewWithTeam(interviewId: string, data: any): Promise<ApiResponse> {
    return this.post(`/interviews/${interviewId}/share/team`, data);
  }

  async shareInterviewWithUser(interviewId: string, data: any): Promise<ApiResponse> {
    return this.post(`/interviews/${interviewId}/share/user`, data);
  }

  async generateInterviewUrl(data: any): Promise<ApiResponse> {
    return this.post('/coding-problems/generate-interview-url', data);
  }

  async getInterviewResponse(interviewId: string): Promise<ApiResponse> {
    return this.get(`/interviews/${interviewId}/response`);
  }

  async getInterviewDataModeling(interviewId: string): Promise<ApiResponse> {
    return this.get(`/interviews/${interviewId}/data-modeling`);
  }

  async getInterviewUmlDesign(interviewId: string): Promise<ApiResponse> {
    return this.get(`/interviews/${interviewId}/uml-design`);
  }

  async getInterviewSystemDesign(interviewId: string): Promise<ApiResponse> {
    return this.get(`/interviews/${interviewId}/system-design`);
  }

  async getInterviewWhiteboard(interviewId: string): Promise<ApiResponse> {
    return this.get(`/interviews/${interviewId}/whiteboard`);
  }

  async getPublicInterviewResponse(responseType: 'system_design' | 'uml_design' | 'whiteboard' | 'data_modeling', interviewId: string): Promise<ApiResponse> {
    return this.get(`/interview-responses/public/load/${responseType}`, { interviewId })
  }

  async getInterviewVisualResponses(interviewId: string): Promise<ApiResponse> {
    return this.get(`/interviews/${interviewId}/visual-responses`);
  }

  async getInterviewDatabaseChallenges(interviewId: string): Promise<ApiResponse> {
    return this.get(`/interviews/${interviewId}/database-challenges`);
  }

  async getInterviewCodingChallenges(interviewId: string): Promise<ApiResponse> {
    return this.get(`/interviews/${interviewId}/coding-challenges`);
  }

  // Evaluations
  async getEvaluation(sourceTable: string, sourceRecordUuid: string): Promise<ApiResponse> {
    return this.get(`/candidate-evaluations/${sourceTable}/${sourceRecordUuid}`)
  }

  async getInterviewEvaluations(interviewId: string): Promise<ApiResponse> {
    return this.get(`/candidate-evaluations/interview/${interviewId}`)
  }

  async createEvaluation(data: any): Promise<ApiResponse> {
    return this.post('/candidate-evaluations', data)
  }

  async updateEvaluation(evaluationUuid: string, data: any): Promise<ApiResponse> {
    return this.put(`/candidate-evaluations/${evaluationUuid}`, data)
  }

  async updateInterview(interviewId: string, data: any): Promise<ApiResponse> {
    return this.put(`/interviews/${interviewId}`, data);
  }

  /**
   * Skills
   */
  async getSkills(): Promise<ApiResponse> {
    return this.get('/skills/list');
  }

  /**
   * Database Challenges
   */
  async getDatabaseChallenges(params?: any): Promise<ApiResponse> {
    return this.get('/database-challenges/', params);
  }

  async submitDatabaseChallenge(data: any): Promise<ApiResponse> {
    return this.post('/database-challenges/submit', data);
  }

  /**
   * Users
   */
  async getUsers(params?: any): Promise<ApiResponse> {
    return this.get('/users/list', params);
  }

  async getAdminRecruiterApplicants(params?: any): Promise<ApiResponse> {
    return this.get('/users/admin/recruiters/applicants', params);
  }

  async updateRecruiterStatus(userUuid: string, status: 'pending_review' | 'approved' | 'hold', message?: string): Promise<ApiResponse> {
    return this.post(`/users/admin/recruiters/${userUuid}/status`, { status, message });
  }

  async messageRecruiterApplicant(userUuid: string, payload: { message: string; subject?: string }): Promise<ApiResponse> {
    return this.post(`/users/admin/recruiters/${userUuid}/message`, payload);
  }

  async getCurrentUser(): Promise<ApiResponse> {
    return this.get('/users/me');
  }

  /**
   * Dashboard
   */
  async getDashboardStats(): Promise<ApiResponse> {
    return this.get('/dashboard/stats');
  }

  /**
   * Positions
   */
  async getPositions(params?: any): Promise<ApiResponse> {
    // Use /positions/list to avoid Mangum base route bug (405 error)
    return this.get('/positions/list', params);
  }

  async getPosition(positionId: string): Promise<ApiResponse> {
    return this.get(`/positions/${positionId}`);
  }

  async createPosition(data: any): Promise<ApiResponse> {
    return this.post('/positions/create', data);
  }

  async updatePosition(positionId: string, data: any): Promise<ApiResponse> {
    return this.put(`/positions/${positionId}`, data);
  }

  async sharePositionWithTeam(positionId: string, data: any): Promise<ApiResponse> {
    return this.post('/pos-shares', {
      position_id: positionId,
      share_type: 'team',
      target_id: data.teamId || data.team_id,
      notes: data.notes || data.message
    });
  }

  async sharePositionWithUser(positionId: string, data: any): Promise<ApiResponse> {
    return this.post('/pos-shares', {
      position_id: positionId,
      share_type: 'individual',
      target_id: data.userId || data.user_id,
      notes: data.notes || data.message
    });
  }

  async getPositionShares(positionId: string): Promise<ApiResponse> {
    return this.get(`/pos-shares/position/${positionId}`)
  }

  async removePositionShare(shareId: string | number): Promise<ApiResponse> {
    return this.delete(`/pos-shares/${shareId}`);
  }

  async getPositionCandidates(positionId: string): Promise<ApiResponse> {
    return this.get(`/positions/${positionId}/candidates`);
  }

  async getPublicJobs(params?: any): Promise<ApiResponse> {
    // Use /public/jobs/list to avoid Mangum base route bug (405 error)
    return this.get('/public/jobs/list', params);
  }

  /**
   * Candidates
   */
  async getCandidates(params?: any): Promise<ApiResponse> {
    // Use /candidates/list to avoid Mangum base route bug (405 error)
    return this.get('/candidates/list', params);
  }

  async getCandidate(candidateId: string): Promise<ApiResponse> {
    return this.get(`/candidates/${candidateId}`);
  }

  async updateCandidate(candidateId: string, data: any): Promise<ApiResponse> {
    return this.put(`/candidates/${candidateId}`, data);
  }

  async validateCandidateEmail(email: string): Promise<ApiResponse> {
    return this.post('/candidates/validate-email', { email });
  }

  async createCandidateFromForm(data: any): Promise<ApiResponse> {
    return this.post('/candidates/form', data);
  }

  async followCandidate(candidateUuid: string): Promise<ApiResponse> {
    return this.post(`/candidates/${candidateUuid}/follow`);
  }

  async unfollowCandidate(candidateUuid: string): Promise<ApiResponse> {
    return this.delete(`/candidates/${candidateUuid}/follow`);
  }

  async getCandidateApplications(email?: string): Promise<ApiResponse> {
    const params = email ? { email } : undefined;
    return this.get('/applications/candidate-applications', params);
  }

  /**
   * Coding Challenges
   */
  async getCodingProblems(params?: any): Promise<ApiResponse> {
    return this.get('/coding-problems/list', params);
  }

  async getCodingProblemCategories(): Promise<ApiResponse> {
    return this.get('/coding-problems/categories');
  }

  async createCodingProblem(data: any): Promise<ApiResponse> {
    return this.post('/coding-problems/create', data);
  }

  async shareChallengeWithTeam(challengeId: string, data: any): Promise<ApiResponse> {
    return this.post(`/coding-challenges/${challengeId}/share/team`, data);
  }

  async shareChallengeWithUser(challengeId: string, data: any): Promise<ApiResponse> {
    return this.post(`/coding-challenges/${challengeId}/share/user`, data);
  }

  /**
   * Team Invitations
   */
  async sendTeamInvitation(data: any): Promise<ApiResponse> {
    return this.post('/team-invitations', data);
  }

  async getTeamDetails(teamId: string): Promise<ApiResponse> {
    return this.get(`/teams/${teamId}`);
  }

  async getTeamManagementData(teamId: string): Promise<ApiResponse> {
    return this.get(`/teams/${teamId}/management`);
  }

  async getTeamSharedPositions(teamId: string): Promise<ApiResponse> {
    return this.get(`/teams/${teamId}/positions`);
  }

  async updateTeam(teamId: string, data: any): Promise<ApiResponse> {
    return this.put(`/teams/${teamId}`, data);
  }

  async deleteTeam(teamId: string): Promise<ApiResponse> {
    return this.delete(`/teams/${teamId}`);
  }

  async removeTeamMember(teamId: string, memberId: number): Promise<ApiResponse> {
    return this.delete(`/teams/${teamId}/members/${memberId}`);
  }

  async changeTeamMemberRole(teamId: string, memberId: number, role: string): Promise<ApiResponse> {
    return this.put(`/teams/${teamId}/members/${memberId}/role`, { role });
  }

  /**
   * Notifications
   */
  async getNotifications(params?: any): Promise<ApiResponse> {
    // Use /notifications/list to avoid Mangum base route bug (405 error)
    return this.get('/notifications/list', params);
  }

  async markNotificationAsRead(notificationId: string): Promise<ApiResponse> {
    return this.patch(`/notifications/${notificationId}/read`);
  }

  async markAllNotificationsAsRead(): Promise<ApiResponse> {
    return this.patch('/notifications/read-all');
  }

  /**
   * Export
   */
  async exportData(type: string, params?: any): Promise<ApiResponse> {
    return this.get(`/export/${type}`, params);
  }

  /**
   * Resume Upload for Profile
   */
  async uploadResumeForProfile(file: File, persist: boolean = false): Promise<ApiResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('persist', persist.toString());

    // Get auth token
    const token = this.getAuthToken();

    // Build full URL
    const url = `${this.config.baseUrl}/resume-parser/extract-for-profile`;

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      console.log(`[API] POST ${url}`);

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
        credentials: this.config.credentials,
        mode: 'cors' as RequestMode,
      });

      // Handle non-JSON responses
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const text = await response.text();
        return {
          success: response.ok,
          data: text as any,
          message: response.ok ? 'Success' : 'Error'
        };
      }

      const data = await response.json();

      // Handle API errors
      if (!response.ok) {
        return {
          success: false,
          error: data.detail || data.error || data.message || `HTTP ${response.status}`,
          message: data.message
        };
      }

      return {
        success: true,
        data: data.data || data,
        message: data.message
      };
    } catch (error) {
      console.error('[API] Resume upload failed:', error);

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        message: 'Resume upload failed'
      };
    }
  }

  /**
   * Upload Resume for Candidate (by recruiter)
   */
  async uploadCandidateResume(candidateId: string, formData: FormData): Promise<ApiResponse> {
    // Get auth token
    const token = this.getAuthToken();

    // Build full URL
    const url = `${this.config.baseUrl}/candidates/${candidateId}/resume`;

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      console.log(`[API] POST ${url}`);

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
        credentials: this.config.credentials,
        mode: 'cors' as RequestMode,
      });

      // Handle non-JSON responses
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const text = await response.text();
        return {
          success: response.ok,
          data: text as any,
          message: response.ok ? 'Success' : 'Error'
        };
      }

      const data = await response.json();

      // Handle API errors
      if (!response.ok) {
        return {
          success: false,
          error: data.detail || data.error || data.message || `HTTP ${response.status}`,
          message: data.message
        };
      }

      return {
        success: true,
        data: data.data || data,
        message: data.message
      };
    } catch (error) {
      console.error('[API] Candidate resume upload failed:', error);

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        message: 'Candidate resume upload failed'
      };
    }
  }

  /**
   * Update Candidate Skills (by recruiter)
   */
  async updateCandidateSkills(candidateId: string, skillsData: { skills: string[] }): Promise<ApiResponse> {
    return this.put(`/candidates/${candidateId}/skills`, skillsData);
  }
}

// Create singleton instance
const apiClient = new ApiClient();

// Export default instance and class
export default apiClient;
export { ApiClient, type ApiResponse };

// Export convenience methods
export const api = {
  get: apiClient.get.bind(apiClient),
  post: apiClient.post.bind(apiClient),
  put: apiClient.put.bind(apiClient),
  delete: apiClient.delete.bind(apiClient),
  patch: apiClient.patch.bind(apiClient),
  getAdminRecruiterApplicants: apiClient.getAdminRecruiterApplicants.bind(apiClient),
  updateRecruiterStatus: apiClient.updateRecruiterStatus.bind(apiClient),
  messageRecruiterApplicant: apiClient.messageRecruiterApplicant.bind(apiClient),
  setAuthToken: apiClient.setAuthToken.bind(apiClient),
  clearAuthToken: apiClient.clearAuthToken.bind(apiClient),
};
